import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  BENCHMARK_COMPARE_ROWS,
  CLIENT_REPORTING_BENCHMARKS,
} from './clientReportingBenchmarkConfig';
import { EMPTY_BENCHMARK_METRICS } from './clientReportingBenchmarkMetrics';
import { fmtCurrency, fmtPct, formatQuoteNumber, safeNumber, sentimentClass } from './clientReportingFormat';
import { IconInfo } from '../Icons';
import { HoverTip, useHoverTip } from './clientReportingHoverTip';
import { buildBenchmarkHelp } from './clientReportingBenchmarkHelp';

/** Hover content for one card: what each figure on it actually measures. */
function BenchmarkHelp({ help }) {
  if (!help) return null;
  return (
    <>
      <div className="vd-ml-quote-tip-head">
        <span className="vd-ml-quote-tip-title">{help.title}</span>
        {help.subtitle ? <span className="vd-ml-quote-tip-sub">{help.subtitle}</span> : null}
      </div>
      <dl className="vd-ml-quote-tip-rows">
        {help.rows.map((row) => (
          <div className="vd-ml-benchmark-help-row" key={row.id}>
            <dt>{row.label}</dt>
            <dd>{row.help}</dd>
          </div>
        ))}
      </dl>
      <p className="vd-ml-quote-tip-note">{help.footnote}</p>
    </>
  );
}

function formatMetricValue(value, kind) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  if (kind === 'currency') return fmtCurrency(value, { signed: true });
  const num = Number(value);
  if (Math.abs(num) >= 10000) {
    const sign = kind !== 'drawdown' && num > 0 ? '+' : '';
    return `${sign}${(num / 1000).toFixed(1)}k%`;
  }
  return fmtPct(value, { signed: kind !== 'drawdown' });
}

function MetricCell({ value, kind, compareTone = false }) {
  const tone = compareTone && kind === 'pct'
    ? sentimentClass(value)
    : (kind === 'drawdown' ? 'vd-negative' : sentimentClass(value));
  return (
    <span className={`vd-ml-benchmark-metric-value ${tone}`}>
      {formatMetricValue(value, kind)}
    </span>
  );
}

export default function ClientReportingBenchmarkBar({
  quotesBySymbol = {},
  quotesLoading = false,
  quotesWarning = '',
  benchmarkMetricsById = {},
  youMetrics = EMPTY_BENCHMARK_METRICS,
  youSummary = { equity: null, changeAmount: null, changePct: null },
  selectedBenchmarkIds = new Set(),
  onToggleBenchmark,
  benchmarkLoading = false,
  benchmarkWarning = '',
  youLabel = 'You',
}) {
  const warning = quotesWarning || benchmarkWarning;
  const loading = quotesLoading || benchmarkLoading;
  // Click, not hover. The cards are toggles that add a line to the chart, so a
  // hover panel opened itself whenever the reader was simply reaching for the
  // toggle — and covered the neighbouring cards they were comparing against.
  // An explicit icon means the explanation appears only when asked for.
  const tip = useHoverTip({ width: 300, height: 330 });
  const rootRef = useRef(null);

  const toggleHelp = useCallback((key, event) => {
    event.stopPropagation();
    event.preventDefault();
    if (tip.activeKey === key) tip.clear();
    else tip.showAtElement(key, event);
  }, [tip]);

  useEffect(() => {
    if (!tip.activeKey) return undefined;
    // The panel itself is pointer-transparent, so any click at all dismisses
    // it — including one on a different card's icon, which then reopens.
    const onDocClick = () => tip.clear();
    const onEscape = (e) => { if (e.key === 'Escape') tip.clear(); };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [tip]);

  const activeHelp = useMemo(() => {
    if (!tip.activeKey) return null;
    if (tip.activeKey === '__you__') {
      return buildBenchmarkHelp({ isYou: true, label: youLabel, rows: BENCHMARK_COMPARE_ROWS });
    }
    const benchmark = CLIENT_REPORTING_BENCHMARKS.find((b) => b.id === tip.activeKey);
    if (!benchmark) return null;
    return buildBenchmarkHelp({
      label: benchmark.label,
      subtitle: benchmark.shortLabel,
      rows: BENCHMARK_COMPARE_ROWS,
    });
  }, [tip.activeKey, youLabel]);
  const youChangePositive = youSummary.changePct !== null ? youSummary.changePct >= 0 : null;

  return (
    <div className="vd-ml-benchmark-strip-wrap">
      {warning ? (
        <p className="vd-ml-benchmark-warning">{warning}</p>
      ) : null}

      {/* The definitions used to sit here as a four-line paragraph. Everyone
          read it once and never again, and it pushed the numbers down the page
          — so it moved onto the cards themselves, where the question is
          actually asked. */}
      <p className="vd-ml-benchmark-hint">
        Click a benchmark to compare it on the chart above.
        {' '}
        <strong>Tap the info icon on a card to see what each figure measures.</strong>
      </p>

      <div className="vd-ml-benchmark-row">
        <div className="vd-ml-benchmark-grid" aria-label="Benchmark comparison">
          {CLIENT_REPORTING_BENCHMARKS.map((benchmark) => {
            const quote = quotesBySymbol[benchmark.id];
            const pct = safeNumber(quote?.percentChange);
            const net = safeNumber(quote?.netChange);
            const isPositive = pct !== null ? pct >= 0 : null;
            const hasQuote = quote?.lastPrice != null;
            const benchmarkMetrics = benchmarkMetricsById[benchmark.id] || EMPTY_BENCHMARK_METRICS;
            const isActive = selectedBenchmarkIds.has(benchmark.id);

            return (
              <button
                key={benchmark.id}
                type="button"
                className={`vd-ml-benchmark-card vd-ml-benchmark-card--${benchmark.accent}${isActive ? ' is-active' : ''}`}
                onClick={() => onToggleBenchmark?.(benchmark.id)}
                aria-pressed={isActive}
                title={isActive ? `Hide ${benchmark.shortLabel} on chart` : `Show ${benchmark.shortLabel} on chart`}
              >
                <div className="vd-ml-benchmark-card-head">
                  <div className="vd-ml-benchmark-card-title">
                    <span className="vd-ml-benchmark-card-symbol">{benchmark.shortLabel}</span>
                    <span className="vd-ml-benchmark-card-name">
                      {benchmark.label} ({benchmark.shortLabel})
                    </span>
                  </div>
                  <span className="vd-ml-benchmark-card-actions">
                    {isActive ? (
                      <span className="vd-ml-benchmark-card-badge">On chart</span>
                    ) : null}
                    {/* Not nested in the card button — a button inside a button
                        is invalid, and the click must not also toggle the
                        chart line. Rendered as a focusable span instead. */}
                    <span
                      role="button"
                      tabIndex={0}
                      className={`vd-ml-benchmark-help-btn${tip.activeKey === benchmark.id ? ' active' : ''}`}
                      onClick={(e) => toggleHelp(benchmark.id, e)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') toggleHelp(benchmark.id, e);
                      }}
                      aria-label={`What the ${benchmark.shortLabel} figures mean`}
                      aria-expanded={tip.activeKey === benchmark.id}
                    >
                      <IconInfo size={14} color="currentColor" />
                    </span>
                  </span>
                </div>

                <div className="vd-ml-benchmark-pill-quote vd-ml-benchmark-card-quote">
                  {quotesLoading && !hasQuote ? (
                    <span className="vd-ml-benchmark-pill-loading">…</span>
                  ) : hasQuote ? (
                    <>
                      <strong>{formatQuoteNumber(quote.lastPrice)}</strong>
                      <span
                        className={`vd-ml-benchmark-pill-chg ${
                          isPositive === null ? '' : isPositive ? 'positive' : 'negative'
                        }`}
                      >
                        {net !== null
                          ? `${net >= 0 ? '+' : ''}${formatQuoteNumber(net)}`
                          : '—'}
                        {' · '}
                        {pct !== null
                          ? `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`
                          : '—'}
                      </span>
                      {quote?.source === 'fred' ? (
                        <span
                          className="vd-ml-benchmark-pill-eod"
                          title="End-of-day index level; one update per business day after the close."
                        >
                          EOD
                        </span>
                      ) : null}
                    </>
                  ) : (
                    <span className="vd-ml-benchmark-pill-loading">—</span>
                  )}
                </div>

                <div className={`vd-ml-benchmark-compare${loading ? ' vd-ml-benchmark-compare-loading' : ''}`}>
                  <div className="vd-ml-benchmark-compare-head">
                    <span className="vd-ml-benchmark-compare-head-spacer" aria-hidden="true" />
                    <span className="vd-ml-benchmark-compare-col-head">Benchmark</span>
                  </div>
                  {BENCHMARK_COMPARE_ROWS.map((row) => (
                    <div className="vd-ml-benchmark-compare-row" key={row.id}>
                      <span className="vd-ml-benchmark-compare-label">{row.label}</span>
                      <div className="vd-ml-benchmark-compare-value">
                        <MetricCell value={benchmarkMetrics[row.id]} kind={row.kind} />
                      </div>
                    </div>
                  ))}
                </div>
              </button>
            );
          })}
        </div>

        <aside
          className="vd-ml-benchmark-you-card"
          aria-label={`${youLabel} performance metrics`}
          ref={rootRef}
        >
          <div className="vd-ml-benchmark-you-head">
            <span className="vd-ml-benchmark-you-title">{youLabel}</span>
            <button
              type="button"
              className={`vd-ml-benchmark-help-btn${tip.activeKey === '__you__' ? ' active' : ''}`}
              onClick={(e) => toggleHelp('__you__', e)}
              aria-label={`What the ${youLabel} figures mean`}
              aria-expanded={tip.activeKey === '__you__'}
            >
              <IconInfo size={14} color="currentColor" />
            </button>
          </div>

          <div className="vd-ml-benchmark-pill-quote vd-ml-benchmark-you-quote">
            {loading && youSummary.equity == null ? (
              <span className="vd-ml-benchmark-pill-loading">…</span>
            ) : youSummary.equity != null ? (
              <>
                <strong>{formatQuoteNumber(youSummary.equity)}</strong>
                <span
                  className={`vd-ml-benchmark-pill-chg ${
                    youChangePositive === null ? '' : youChangePositive ? 'positive' : 'negative'
                  }`}
                >
                  {youSummary.changeAmount != null
                    ? fmtCurrency(youSummary.changeAmount, { signed: true, compact: true })
                    : '—'}
                  {' · '}
                  {youSummary.changePct != null
                    ? fmtPct(youSummary.changePct, { signed: true })
                    : '—'}
                </span>
              </>
            ) : (
              <span className="vd-ml-benchmark-pill-loading">—</span>
            )}
          </div>

          <div className={`vd-ml-benchmark-compare vd-ml-benchmark-compare--you${loading ? ' vd-ml-benchmark-compare-loading' : ''}`}>
            <div className="vd-ml-benchmark-compare-head vd-ml-benchmark-compare-head--spacer" aria-hidden="true">
              <span className="vd-ml-benchmark-compare-head-spacer" />
              <span className="vd-ml-benchmark-compare-col-head">&nbsp;</span>
            </div>
            {BENCHMARK_COMPARE_ROWS.map((row) => (
              <div className="vd-ml-benchmark-compare-row" key={row.id}>
                <span className="vd-ml-benchmark-compare-label">{row.label}</span>
                <div className="vd-ml-benchmark-compare-value">
                  <MetricCell value={youMetrics[row.id]} kind={row.kind} compareTone />
                </div>
              </div>
            ))}
          </div>
        </aside>
      </div>

      <HoverTip position={tip.position} width={300} className="vd-ml-quote-tip vd-ml-benchmark-help">
        <BenchmarkHelp help={activeHelp} />
      </HoverTip>
    </div>
  );
}
